import { AriaDescriber, FocusMonitor } from '@angular/cdk/a11y';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import {
  ConnectedPosition,
  Overlay,
  OverlayConfig,
  OverlayPositionBuilder,
  OverlayRef,
} from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  booleanAttribute,
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  NgZone,
  numberAttribute,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { TooltipContentComponent } from './tooltip-content.component';
import { TOOLTIP_PANEL_CLASS, TooltipPosition } from './tooltip.types';

@Directive({
  selector: '[appTooltip]',
  exportAs: 'appTooltip',
  host: {
    '(mouseenter)': 'show()',
    '(mouseleave)': 'hide()',
  },
})
export class TooltipDirective implements OnInit, OnChanges, OnDestroy {
  @Input('appTooltip') message = '';
  @Input('appTooltipPosition') position: TooltipPosition = 'below';
  @Input() appTooltipClass: string | string[] = '';

  @Input({ alias: 'appTooltipShowDelay', transform: numberAttribute })
  showDelay = 0;
  @Input({ alias: 'appTooltipDisabled', transform: booleanAttribute })
  disabled = false;

  private overlayRef: OverlayRef | null = null;
  private contentRef: ComponentRef<TooltipContentComponent> | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;
  private focusSubscription: Subscription | null = null;
  private currentDescription: string | null = null;

  constructor(
    private readonly overlay: Overlay,
    private readonly overlayPositionBuilder: OverlayPositionBuilder,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly ngZone: NgZone,
    private readonly ariaDescriber: AriaDescriber,
    private readonly focusMonitor: FocusMonitor
  ) {}

  ngOnInit(): void {
    this.syncAriaDescription();
    this.focusSubscription = this.focusMonitor
      .monitor(this.elementRef)
      .subscribe((origin) => {
        if (origin === 'keyboard' || origin === 'program') {
          this.ngZone.run(() => this.show());
        } else if (origin === null) {
          this.ngZone.run(() => this.hide());
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['message'] || changes['disabled']) {
      this.syncAriaDescription();
      if (this.isSuppressed()) {
        this.hide();
      } else if (this.contentRef) {
        this.contentRef.instance.message = this.message;
        this.contentRef.changeDetectorRef.markForCheck();
      }
    }
  }

  ngOnDestroy(): void {
    this.clearTimer();
    this.focusSubscription?.unsubscribe();
    this.focusMonitor.stopMonitoring(this.elementRef);
    this.removeAriaDescription();
    this.detach();
  }

  show(): void {
    if (this.isSuppressed()) {
      return;
    }
    this.clearTimer();
    // Already open (or mid-leave): cancel the leave and keep it visible.
    if (this.contentRef) {
      this.contentRef.instance.show();
      return;
    }
    this.showTimer = setTimeout(() => {
      this.showTimer = null;
      this.attach();
    }, this.showDelay);
  }

  hide(): void {
    this.clearTimer();
    if (this.contentRef) {
      this.contentRef.instance.startLeave();
    } else {
      this.detach();
    }
  }

  private attach(): void {
    if (this.overlayRef) {
      return;
    }
    this.overlayRef = this.overlay.create(this.buildConfig());
    this.overlayRef
      .keydownEvents()
      .subscribe((event) => this.onOverlayKeydown(event));
    const portal = new ComponentPortal(
      TooltipContentComponent,
      this.viewContainerRef
    );
    this.contentRef = this.overlayRef.attach(portal);
    // The tooltip must never intercept pointer events from the trigger.
    this.overlayRef.overlayElement.style.pointerEvents = 'none';
    this.contentRef.instance.afterLeave.subscribe(() => this.detach());
    this.contentRef.instance.message = this.message;
    this.contentRef.changeDetectorRef.detectChanges();
  }

  private onOverlayKeydown(event: KeyboardEvent): void {
    if (
      (event.keyCode === ESCAPE || event.key === 'Escape') &&
      !hasModifierKey(event)
    ) {
      event.preventDefault();
      this.ngZone.run(() => this.hide());
    }
  }

  private detach(): void {
    this.contentRef = null;
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  private buildConfig(): OverlayConfig {
    const positionStrategy = this.overlayPositionBuilder
      .flexibleConnectedTo(this.elementRef)
      .withPositions(this.getPositions())
      .withFlexibleDimensions(false)
      .withPush(true);

    return new OverlayConfig({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: [TOOLTIP_PANEL_CLASS, ...this.normalizePanelClass()],
      hasBackdrop: false,
    });
  }

  private getPositions(): ConnectedPosition[] {
    switch (this.position) {
      case 'above':
        return [
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
          },
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
        ];
      case 'left':
        return [
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
          },
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
          },
        ];
      case 'right':
        return [
          {
            originX: 'end',
            originY: 'center',
            overlayX: 'start',
            overlayY: 'center',
          },
          {
            originX: 'start',
            originY: 'center',
            overlayX: 'end',
            overlayY: 'center',
          },
        ];
      case 'below':
      default:
        return [
          {
            originX: 'center',
            originY: 'bottom',
            overlayX: 'center',
            overlayY: 'top',
          },
          {
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom',
          },
        ];
    }
  }

  private normalizePanelClass(): string[] {
    if (!this.appTooltipClass) {
      return [];
    }
    return Array.isArray(this.appTooltipClass)
      ? this.appTooltipClass.filter(Boolean)
      : [this.appTooltipClass];
  }

  private isSuppressed(): boolean {
    return this.disabled || !this.message || !this.message.trim();
  }

  private syncAriaDescription(): void {
    const next = this.isSuppressed() ? null : this.message;
    if (next === this.currentDescription) {
      return;
    }
    this.removeAriaDescription();
    if (next) {
      this.ariaDescriber.describe(
        this.elementRef.nativeElement,
        next,
        'tooltip'
      );
      this.currentDescription = next;
    }
  }

  private removeAriaDescription(): void {
    if (this.currentDescription) {
      this.ariaDescriber.removeDescription(
        this.elementRef.nativeElement,
        this.currentDescription,
        'tooltip'
      );
      this.currentDescription = null;
    }
  }

  private clearTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }
}
